import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Header from './Header';
import { ChevronDown, ChevronUp, Clock, Info, Package, X } from 'lucide-react';
import ProductCard from './ProductCard';
import OrderForm from './OrderForm';
import ProductDetailModal from './ProductDetailModal';
import MyInfoModal from './MyInfoModal';
import StoreInfoModal from './StoreInfoModal';
import Footer from './Footer';
import {
  Product,
  Store,
  UserProfile,
  OrderSheet,
  OrderItem,
  Tag,
  Order,
  UnavailableOrderItem,
  WaitlistEntry,
} from '../types';
import { createOrder, getMyOrders } from '../api/orders';
import {
  cancelWaitlist,
  getMyWaitlists,
  joinWaitlist,
} from '../api/waitlists';
import {
  getOrderFunnelSessionId,
  getStoreEntryFunnelScope,
  resetOrderFunnelSessionId,
  sendOrderFunnelBeacon,
  trackOrderFunnelEvent,
} from '../api/order-funnel';
import { updateProfile } from '../api/auth';
import { getCustomerOrderPage } from '../api/customer-pages';
import { usePickupPreference } from '../hooks/usePickupPreference';
import { useStoreRequestPreference } from '../hooks/useStoreRequestPreference';
import NotFoundPage from './NotFoundPage';
import {
  buildPickupDateOptions,
  buildPickupHourOptions,
  formatStoreOperationSummary,
  getStorePickupHourExtraLabel,
  shouldCollectStorePickupTime,
  shouldCollectStoreRequestMessage,
} from '../utils/storeOperation';
import {
  buildStoreOrderRequestText,
  buildStoredPickupLabel,
  parseStoreOrderRequestText,
} from '../utils/storePickup';
import {
  formatArrivalDateLabel,
  sortArrivalDateValues,
} from '../utils/arrivalDates';
import shareKakao from '../utils/shareKakao';
import {
  SHARE_TOKEN_PARAM,
  SHARED_PRODUCT_PARAM,
  createShareToken,
  readShareAttribution,
} from '../utils/shareTracking';

interface OrderPageProps {
  storeId: string;
  buyerProfile: UserProfile & { id?: string };
  setBuyerProfile: React.Dispatch<
    React.SetStateAction<UserProfile & { id?: string }>
  >;
  onViewHistory: () => void;
  onViewCart: () => void;
  onLogout: () => void;
}

const buildCartFromOrders = (orders: Order[]) =>
  orders.reduce<Record<string, number>>((acc, order) => {
    order.items
      .filter((item) => item.quantity > 0)
      .forEach((item) => {
        const key = item.productOptionId || item.productId;
        acc[key] = (acc[key] || 0) + item.quantity;
      });

    return acc;
  }, {});

const buildOrderItemMapFromOrders = (orders: Order[]) =>
  orders.reduce<Record<string, OrderItem>>((acc, order) => {
    order.items
      .filter((item) => item.quantity > 0)
      .forEach((item) => {
        const key = item.productOptionId || item.productId;
        if (!acc[key]) {
          acc[key] = { ...item };
        }
      });

    return acc;
  }, {});

const buildOrderItemMapFromItems = (items: OrderItem[]) =>
  items.reduce<Record<string, OrderItem>>((acc, item) => {
    const key = item.productOptionId || item.productId;
    acc[key] = { ...item };
    return acc;
  }, {});

const buildCartFromItems = (items: OrderItem[]) =>
  items.reduce<Record<string, number>>((acc, item) => {
    const key = item.productOptionId || item.productId;
    acc[key] = item.quantity;
    return acc;
  }, {});

const getOrderItemCartKey = (item: Pick<OrderItem, 'productId' | 'productOptionId'>) =>
  item.productOptionId || item.productId || '';

const mergeCartQuantities = (
  baseCart: Record<string, number>,
  addedCart: Record<string, number>,
) => {
  const nextCart = { ...baseCart };

  Object.entries(addedCart).forEach(([id, qty]) => {
    nextCart[id] = (nextCart[id] || 0) + qty;
  });

  return nextCart;
};

const formatUnavailableItemsMessage = (items: UnavailableOrderItem[]) =>
  items
    .map((item) => {
      const availabilityLabel =
        item.availableQuantity > 0
          ? `요청 ${item.requestedQuantity}개 / 가능 ${item.availableQuantity}개`
          : item.reason === 'OUT_OF_STOCK'
            ? `요청 ${item.requestedQuantity}개 / 품절`
            : `요청 ${item.requestedQuantity}개 / 주문 불가`;
      return `- ${item.name} (${availabilityLabel})`;
    })
    .join('\n');

const getOrderPreviewOptionLabel = (
  item: OrderItem,
  products: Product[],
) => {
  if (item.optionName?.trim()) {
    return item.optionName.trim();
  }

  if (!item.productOptionId) {
    return '';
  }

  const product = products.find((candidate) => candidate.id === item.productId);
  return (
    product?.options?.find((option) => option.id === item.productOptionId)?.name
    || ''
  );
};

const getOrderPreviewName = (
  item: OrderItem,
  optionLabel: string,
) => {
  if (!optionLabel) {
    return item.name;
  }

  const suffix = ` (${optionLabel})`;
  return item.name.endsWith(suffix)
    ? item.name.slice(0, -suffix.length)
    : item.name;
};

const sanitizeImageUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const OrderPage: React.FC<OrderPageProps> = ({
  storeId,
  buyerProfile,
  setBuyerProfile,
  onViewHistory,
  onViewCart,
  onLogout,
}) => {
  type SubmitDestination = 'stay' | 'history';
  type OrderConfirmMode = 'confirm' | 'success';
  type OrderSubmitOverrides = {
    sourceCart?: Record<string, number>;
    profile?: UserProfile & { id?: string };
    pickupDate?: string;
    pickupHour?: number | '';
    requestMessage?: string;
  };

  const [products, setProducts] = useState<Product[]>([]);
  const [waitlistsByKey, setWaitlistsByKey] = useState<
    Record<string, WaitlistEntry>
  >({});
  const [waitlistQuantities, setWaitlistQuantities] = useState<
    Record<string, number>
  >({});
  const [updatingWaitlistKey, setUpdatingWaitlistKey] = useState<string | null>(
    null,
  );
  const [cart, setCart] = useState<Record<string, number>>({});
  const [existingOrderCart, setExistingOrderCart] = useState<
    Record<string, number>
  >({});
  const [existingOrderItems, setExistingOrderItems] = useState<
    Record<string, OrderItem>
  >({});
  const [currentEditableOrderSheetId, setCurrentEditableOrderSheetId] =
    useState<string | null>(null);
  const [isLoadingExistingOrders, setIsLoadingExistingOrders] = useState(false);
  const [isMyInfoOpen, setIsMyInfoOpen] = useState(false);
  const [isStoreInfoOpen, setIsStoreInfoOpen] = useState(false);
  const [selectedProductForDetail, setSelectedProductForDetail] =
    useState<Product | null>(null);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [activeArrivalDate, setActiveArrivalDate] = useState<string | null>(
    null,
  );
  const [activeOrderSheet, setActiveOrderSheet] = useState<OrderSheet | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPickupDate, setSelectedPickupDate] = useState('');
  const [selectedPickupHour, setSelectedPickupHour] = useState<number | ''>('');
  const [savedPickupLabel, setSavedPickupLabel] = useState<string | null>(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [isOrderConfirmSheetOpen, setIsOrderConfirmSheetOpen] = useState(false);
  const [orderConfirmMode, setOrderConfirmMode] =
    useState<OrderConfirmMode>('confirm');
  const [orderSuccessMessage, setOrderSuccessMessage] = useState('');
  const [orderSuccessSummary, setOrderSuccessSummary] = useState<{
    items: OrderItem[];
    totalCount: number;
    totalPrice: number;
  } | null>(null);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [submitDestination, setSubmitDestination] =
    useState<SubmitDestination | null>(null);
  const [draftBuyerProfile, setDraftBuyerProfile] = useState<UserProfile>({
    name: '',
    phone: '',
  });
  const [draftPickupDate, setDraftPickupDate] = useState('');
  const [draftPickupHour, setDraftPickupHour] = useState<number | ''>('');
  const [draftRequestMessage, setDraftRequestMessage] = useState('');
  const [isOrderSheetSummaryExpanded, setIsOrderSheetSummaryExpanded] =
    useState(false);
  const orderConfirmNameInputRef = useRef<HTMLInputElement | null>(null);
  const orderConfirmPhoneInputRef = useRef<HTMLInputElement | null>(null);
  const trackedKakaoLinkClicksRef = useRef<Set<string>>(new Set());
  const trackedOrderSheetViewsRef = useRef<Set<string>>(new Set());
  const autoOpenedProductIdRef = useRef<string | null>(null);
  const cartUpdateTimerRef = useRef<number | null>(null);
  const customerInfoTimerRef = useRef<number | null>(null);
  const lastCustomerInfoEventKeyRef = useRef('');

  const [isLoadingStore, setIsLoadingStore] = useState(true);
  const [storeError, setStoreError] = useState<string | null>(null);
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const resolvedStoreId = currentStore?.id || storeId;
  const isStoreOrder = currentStore?.orderType === 'STORE';
  const displayProducts = useMemo(
    () =>
      products.map((product) => ({
        ...product,
        arrivalDateValues: sortArrivalDateValues(product.arrivalDateValues),
      })),
    [products],
  );

  const hasArrivalDateProducts = useMemo(
    () => displayProducts.some((product) => (product.arrivalDateValues?.length ?? 0) > 0),
    [displayProducts],
  );
  const isArrivalDatePriorityStore =
    isStoreOrder && currentStore?.supportsArrivalDateFilter === true;
  const isArrivalDateFilterEnabled =
    isArrivalDatePriorityStore && hasArrivalDateProducts;
  const isPickupTimeCollectionEnabled = shouldCollectStorePickupTime(currentStore);
  const isRequestMessageEnabled = shouldCollectStoreRequestMessage(currentStore);
  const {
    pickupSelection: storedPickupSelection,
    savePickupSelection,
  } = usePickupPreference({
    enabled: isPickupTimeCollectionEnabled,
    storeId: resolvedStoreId,
    userId: buyerProfile.id,
  });
  const {
    requestMessage: storedRequestMessage,
    saveRequestMessage,
  } = useStoreRequestPreference({
    enabled: isRequestMessageEnabled,
    storeId: resolvedStoreId,
    userId: buyerProfile.id,
  });

  const loadStoreData = useCallback(
    async ({ showSpinner = false }: { showSpinner?: boolean } = {}) => {
      if (!storeId) {
        setIsLoadingStore(false);
        return;
      }

      if (showSpinner) {
        setIsLoadingStore(true);
      }
      setStoreError(null);
      try {
        const { store, products, activeOrderSheet, tags } =
          await getCustomerOrderPage(storeId);

        if (!store) {
          setStoreError('STORE_NOT_FOUND');
          return;
        }

        setCurrentStore(store);
        setProducts(products);
        setSelectedProductForDetail((current) =>
          current
            ? products.find((product) => product.id === current.id) || current
            : null,
        );
        setActiveOrderSheet(activeOrderSheet);
        setAvailableTags(tags);
      } catch (error) {
        console.error('Failed to fetch store data', error);
        if (showSpinner) {
          setStoreError('FETCH_ERROR');
        }
      } finally {
        if (showSpinner) {
          setIsLoadingStore(false);
        }
      }
    },
    [storeId],
  );

  useEffect(() => {
    loadStoreData({ showSpinner: true });
  }, [loadStoreData]);

  const loadMyWaitlists = useCallback(async () => {
    if (!activeOrderSheet?.id || !resolvedStoreId) {
      setWaitlistsByKey({});
      return;
    }

    try {
      const entries = await getMyWaitlists({
        storeId: resolvedStoreId,
        orderSheetId: activeOrderSheet.id,
      });
      setWaitlistsByKey(
        entries.reduce<Record<string, WaitlistEntry>>((map, entry) => {
          map[entry.productOptionId || entry.productId] = entry;
          return map;
        }, {}),
      );
    } catch (error) {
      console.error('Failed to fetch waitlists', error);
    }
  }, [activeOrderSheet?.id, resolvedStoreId]);

  useEffect(() => {
    void loadMyWaitlists();
  }, [loadMyWaitlists]);

  useEffect(() => {
    if (currentStore) {
      document.title = currentStore.name;
    }
  }, [currentStore]);

  useEffect(() => {
    if (typeof window === 'undefined' || products.length === 0) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const linkedProductId =
      params.get('productId') || params.get(SHARED_PRODUCT_PARAM);

    if (!linkedProductId || autoOpenedProductIdRef.current === linkedProductId) {
      return;
    }

    const linkedProduct = products.find((product) => product.id === linkedProductId);
    if (!linkedProduct || linkedProduct.isHidden) {
      return;
    }

    autoOpenedProductIdRef.current = linkedProductId;
    setSelectedProductForDetail(linkedProduct);
  }, [products]);

  useEffect(() => {
    const slug = currentStore?.slug?.trim();
    if (!slug || slug === storeId || typeof window === 'undefined') {
      return;
    }

    const nextUrl = `/store/${encodeURIComponent(slug)}${window.location.search}${window.location.hash}`;
    if (window.location.pathname !== `/store/${slug}`) {
      window.history.replaceState({}, document.title, nextUrl);
    }
  }, [currentStore?.slug, storeId]);

  const pickupDateOptions = useMemo(() => {
    if (!isPickupTimeCollectionEnabled) {
      return [];
    }

    return buildPickupDateOptions(
      currentStore?.operationDayOfWeeks,
      currentStore?.operationStartHour,
      currentStore?.operationEndHour,
    );
  }, [
    isPickupTimeCollectionEnabled,
    currentStore?.operationDayOfWeeks,
    currentStore?.operationStartHour,
    currentStore?.operationEndHour,
  ]);

  const pickupHourOptions = useMemo(() => {
    if (!isPickupTimeCollectionEnabled) {
      return [];
    }

    return buildPickupHourOptions(
      currentStore?.operationStartHour,
      currentStore?.operationEndHour,
      selectedPickupDate,
      new Date(),
      currentStore?.pickupUnmannedStartHour,
      currentStore?.pickupUnmannedLabel,
    );
  }, [
    isPickupTimeCollectionEnabled,
    currentStore?.operationStartHour,
    currentStore?.operationEndHour,
    currentStore?.pickupUnmannedStartHour,
    currentStore?.pickupUnmannedLabel,
    selectedPickupDate,
  ]);

  const orderConfirmPickupHourOptions = useMemo(() => {
    if (!isPickupTimeCollectionEnabled) {
      return [];
    }

    return buildPickupHourOptions(
      currentStore?.operationStartHour,
      currentStore?.operationEndHour,
      draftPickupDate,
      new Date(),
      currentStore?.pickupUnmannedStartHour,
      currentStore?.pickupUnmannedLabel,
    );
  }, [
    isPickupTimeCollectionEnabled,
    currentStore?.operationStartHour,
    currentStore?.operationEndHour,
    currentStore?.pickupUnmannedStartHour,
    currentStore?.pickupUnmannedLabel,
    draftPickupDate,
  ]);

  const orderConfirmPickupLabel = useMemo(() => {
    if (!isPickupTimeCollectionEnabled) {
      return null;
    }

    const effectivePickupDate = draftPickupDate || pickupDateOptions[0]?.value;

    if (effectivePickupDate && draftPickupHour !== '') {
      return buildStoredPickupLabel(
        effectivePickupDate,
        Number(draftPickupHour),
        getStorePickupHourExtraLabel(currentStore, Number(draftPickupHour)),
      );
    }

    return savedPickupLabel;
  }, [
    pickupDateOptions,
    draftPickupDate,
    draftPickupHour,
    isPickupTimeCollectionEnabled,
    savedPickupLabel,
  ]);

  const storeOperationSummary = useMemo(
    () => (currentStore ? formatStoreOperationSummary(currentStore) : ''),
    [currentStore],
  );

  const orderSheetSummaryLines = useMemo(() => {
    if (isStoreOrder) {
      return [
        storeOperationSummary
          ? {
            label: '운영 안내',
            value: storeOperationSummary,
            valueClassName: 'text-emerald-600',
          }
          : null,
        currentStore?.deliverySchedule
          ? {
            label: '판매자 메시지',
            value: currentStore.deliverySchedule,
            valueClassName: 'text-emerald-600',
          }
          : null,
        isPickupTimeCollectionEnabled && savedPickupLabel
          ? {
            label: '저장된 픽업 일정',
            value: savedPickupLabel,
            valueClassName: 'text-gray-900',
          }
          : null,
      ].filter(Boolean) as {
        label: string;
        value: string;
        valueClassName: string;
      }[];
    }

    return [
      currentStore?.deliverySchedule
        ? {
          label: '판매자 메시지',
          value: currentStore.deliverySchedule,
          valueClassName: 'text-emerald-600',
        }
        : null,
    ].filter(Boolean) as {
      label: string;
      value: string;
      valueClassName: string;
    }[];
  }, [
    currentStore,
    isPickupTimeCollectionEnabled,
    isStoreOrder,
    savedPickupLabel,
    storeOperationSummary,
  ]);

  const hasOrderSheetSummaryDetails = orderSheetSummaryLines.length > 0;
  const orderCartScopeKey = useMemo(
    () =>
      [
        resolvedStoreId,
        activeOrderSheet?.id || '',
        buyerProfile.id || '',
        buyerProfile.name.trim(),
        buyerProfile.phone.trim(),
      ].join('::'),
    [
      activeOrderSheet?.id,
      buyerProfile.id,
      buyerProfile.name,
      buyerProfile.phone,
      resolvedStoreId,
    ],
  );
  const orderFunnelScope = useMemo(
    () => getStoreEntryFunnelScope(resolvedStoreId || storeId),
    [resolvedStoreId, storeId],
  );
  const orderFunnelSessionId = useMemo(
    () => getOrderFunnelSessionId(orderFunnelScope),
    [orderFunnelScope],
  );
  const orderFunnelBasePayload = useMemo(
    () => ({
      sessionId: orderFunnelSessionId,
      storeId: resolvedStoreId,
      orderSheetId: activeOrderSheet?.id,
      userId: buyerProfile.id,
    }),
    [activeOrderSheet?.id, buyerProfile.id, orderFunnelSessionId, resolvedStoreId],
  );

  // 공유 링크로 진입했는지(누가 공유한 링크인지) 어트리뷰션 값을 마운트 시 1회 캡처
  const shareAttribution = useMemo(() => readShareAttribution(), []);

  const trackFunnelEvent = (
    payload: Omit<Parameters<typeof trackOrderFunnelEvent>[0], 'sessionId'>,
  ) => {
    void trackOrderFunnelEvent({
      ...orderFunnelBasePayload,
      ...payload,
      sessionId: orderFunnelSessionId,
    }).catch((error) => {
      console.error('Failed to track order funnel event', error);
    });
  };

  const resolveEditableOrderSnapshot = useCallback(
    async (profile: UserProfile & { id?: string } = buyerProfile) => {
      const profileName = profile.name.trim();
      const profilePhone = profile.phone.trim();

      if (!profile.id && !profileName && !profilePhone) {
        return {
          targetOrderSheetId: null as string | null,
          editableOrders: [] as Order[],
          cart: {} as Record<string, number>,
          items: {} as Record<string, OrderItem>,
        };
      }

      const myOrders = await getMyOrders(profileName, profilePhone, profile.id);
      const activeOrdersForStore = myOrders.filter((order) => {
        if (order.storeId !== resolvedStoreId) return false;
        if (['COMPLETED', 'CANCELLED'].includes(order.status)) return false;
        return true;
      });

      const targetOrderSheetId =
        activeOrderSheet?.id
        || activeOrdersForStore.find((order) => order.orderSheetId)?.orderSheetId
        || null;

      const editableOrders = activeOrdersForStore.filter((order) => {
        if (targetOrderSheetId) {
          return order.orderSheetId === targetOrderSheetId;
        }

        return !order.orderSheetId;
      });

      return {
        targetOrderSheetId,
        editableOrders,
        cart: buildCartFromOrders(editableOrders),
        items: buildOrderItemMapFromOrders(editableOrders),
      };
    },
    [activeOrderSheet?.id, buyerProfile, resolvedStoreId],
  );

  useEffect(() => {
    setIsOrderSheetSummaryExpanded(false);
  }, [currentStore?.id, activeOrderSheet?.id]);

  useEffect(() => {
    if (!currentStore) {
      return;
    }

    const clickKey = `${currentStore.id}:${activeOrderSheet?.id || 'none'}`;
    if (trackedKakaoLinkClicksRef.current.has(clickKey)) {
      return;
    }

    trackedKakaoLinkClicksRef.current.add(clickKey);
    trackFunnelEvent({
      eventType: 'KAKAO_LINK_CLICKED',
      metadata: {
        source: 'kakao',
        landingPath:
          typeof window === 'undefined'
            ? `/store/${storeId}`
            : `${window.location.pathname}${window.location.search}`,
        storeName: currentStore.name,
        orderType: currentStore.orderType || 'DELIVERY',
        hasActiveOrderSheet: Boolean(activeOrderSheet?.id),
        shareToken: shareAttribution.shareToken,
        sharedProductId: shareAttribution.sharedProductId,
      },
    });
  }, [activeOrderSheet?.id, currentStore, orderFunnelBasePayload, storeId]);

  useEffect(() => {
    if (!currentStore) {
      return;
    }

    const viewKey = `${currentStore.id}:${activeOrderSheet?.id || 'none'}`;
    if (trackedOrderSheetViewsRef.current.has(viewKey)) {
      return;
    }

    trackedOrderSheetViewsRef.current.add(viewKey);
    trackFunnelEvent({
      eventType: 'ORDER_SHEET_VIEWED',
      metadata: {
        storeName: currentStore.name,
        orderType: currentStore.orderType || 'DELIVERY',
        hasActiveOrderSheet: Boolean(activeOrderSheet?.id),
      },
    });
  }, [activeOrderSheet?.id, currentStore, orderFunnelBasePayload]);

  useEffect(() => {
    setCart({});
  }, [orderCartScopeKey]);

  useEffect(() => {
    if (!isPickupTimeCollectionEnabled) {
      setSelectedPickupDate('');
      setSelectedPickupHour('');
      setSavedPickupLabel(null);
      return;
    }

    const isStoredDateValid =
      !!storedPickupSelection &&
      pickupDateOptions.some(
        (option) => option.value === storedPickupSelection.dateValue,
      );

    setSelectedPickupDate(
      isStoredDateValid
        ? storedPickupSelection.dateValue
        : pickupDateOptions[0]?.value || '',
    );
    setSavedPickupLabel(isStoredDateValid ? storedPickupSelection.label : null);
  }, [isPickupTimeCollectionEnabled, pickupDateOptions, storedPickupSelection]);

  useEffect(() => {
    if (!isPickupTimeCollectionEnabled) {
      return;
    }

    const isStoredHourValid =
      !!storedPickupSelection &&
      storedPickupSelection.dateValue === selectedPickupDate &&
      pickupHourOptions.some(
        (option) => option.value === storedPickupSelection.hour,
      );

    setSelectedPickupHour(
      isStoredHourValid
        ? storedPickupSelection.hour
        : pickupHourOptions[0]?.value ?? '',
    );

    if (isStoredHourValid && storedPickupSelection?.label) {
      setSavedPickupLabel(storedPickupSelection.label);
    } else if (storedPickupSelection?.label) {
      setSavedPickupLabel(null);
    }
  }, [
    isPickupTimeCollectionEnabled,
    pickupHourOptions,
    selectedPickupDate,
    storedPickupSelection,
  ]);

  useEffect(() => {
    if (!isRequestMessageEnabled) {
      setRequestMessage('');
      return;
    }

    setRequestMessage(storedRequestMessage);
  }, [isRequestMessageEnabled, storedRequestMessage]);

  useEffect(() => {
    if (!isArrivalDateFilterEnabled) {
      setActiveArrivalDate(null);
    }
  }, [isArrivalDateFilterEnabled]);

  useEffect(() => {
    if (!isOrderConfirmSheetOpen || !isPickupTimeCollectionEnabled) {
      return;
    }

    const hasValidHour =
      draftPickupHour !== '' &&
      orderConfirmPickupHourOptions.some(
        (option) => option.value === draftPickupHour,
      );

    if (hasValidHour) {
      return;
    }

    setDraftPickupHour(orderConfirmPickupHourOptions[0]?.value ?? '');
  }, [
    draftPickupHour,
    isOrderConfirmSheetOpen,
    isPickupTimeCollectionEnabled,
    orderConfirmPickupHourOptions,
  ]);

  useEffect(() => {
    if (!currentStore) {
      return;
    }

    if (!buyerProfile.id && !buyerProfile.name.trim() && !buyerProfile.phone.trim()) {
      setCart({});
      setExistingOrderCart({});
      setExistingOrderItems({});
      setCurrentEditableOrderSheetId(null);
      setIsLoadingExistingOrders(false);
      return;
    }

    let isCancelled = false;

    const fetchExistingOrders = async () => {
      setIsLoadingExistingOrders(true);
      try {
        const snapshot = await resolveEditableOrderSnapshot();

        if (isCancelled) {
          return;
        }

        setCurrentEditableOrderSheetId(snapshot.targetOrderSheetId);
        setExistingOrderCart(snapshot.cart);
        setExistingOrderItems(snapshot.items);

        if (
          isStoreOrder &&
          (isPickupTimeCollectionEnabled || isRequestMessageEnabled)
        ) {
          const requestSourceOrder = snapshot.editableOrders.find((order) =>
            order.request?.trim(),
          );

          if (requestSourceOrder?.request) {
            const { pickupLabel, requestMessage: nextRequestMessage } =
              parseStoreOrderRequestText(requestSourceOrder.request);

            if (
              isPickupTimeCollectionEnabled &&
              !storedPickupSelection?.label &&
              pickupLabel
            ) {
              setSavedPickupLabel(pickupLabel);
            }
            if (
              isRequestMessageEnabled &&
              !storedRequestMessage.trim() &&
              nextRequestMessage
            ) {
              setRequestMessage(nextRequestMessage);
            }
          }
        }
      } catch (error) {
        console.error('Failed to preload current order items', error);
      } finally {
        if (!isCancelled) {
          setIsLoadingExistingOrders(false);
        }
      }
    };

    fetchExistingOrders();

    return () => {
      isCancelled = true;
    };
  }, [
    activeOrderSheet?.id,
    buyerProfile.id,
    buyerProfile.name,
    buyerProfile.phone,
    currentStore,
    isStoreOrder,
    isPickupTimeCollectionEnabled,
    isRequestMessageEnabled,
    resolveEditableOrderSnapshot,
    resolvedStoreId,
  ]);

  const isSelectableProduct = (product?: Product | null) =>
    Boolean(product) && !product.isHidden;

  const handleSetQuantity = (id: string, quantity: number) => {
    setCart((prev) => {
      const allOptions = products.flatMap((product) => product.options || []);
      const option = allOptions.find((item) => item.id === id);

      if (option) {
        const parentProduct = products.find((item) => item.id === option.productId);
        if (!isSelectableProduct(parentProduct)) {
          return prev;
        }
        if (option.isClosed) return prev;
        const available = option.availableStock ?? option.stock ?? 0;
        const nextQuantity = Math.max(0, Math.min(available, quantity));
        if (nextQuantity === 0) {
          const { [id]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [id]: nextQuantity };
      }

      const product = products.find((item) => item.id === id);
      if (!product || product.isClosed) return prev;
      if (!isSelectableProduct(product)) {
        return prev;
      }
      const available = product.availableStock ?? product.totalStock ?? 0;
      const nextQuantity = Math.max(0, Math.min(available, quantity));
      if (nextQuantity === 0) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: nextQuantity };
    });
  };

  const handleToggleWaitlist = async (
    productId: string,
    productOptionId?: string,
    quantity = 1,
  ) => {
    if (!currentStore || !activeOrderSheet) {
      alert('현재 줄서기를 신청할 수 있는 공동구매 회차가 없습니다.');
      return;
    }

    if (!buyerProfile.name.trim() || !buyerProfile.phone.trim()) {
      alert('줄서기 신청을 위해 이름과 연락처를 먼저 입력해주세요.');
      setIsMyInfoOpen(true);
      return;
    }

    const waitlistKey = productOptionId || productId;
    const existingEntry = waitlistsByKey[waitlistKey];
    if (
      existingEntry &&
      !window.confirm(
        `남은 ${existingEntry.remainingQuantity}개 줄서기를 취소하시겠습니까? 이미 주문에 반영된 수량은 유지됩니다.`,
      )
    ) {
      return;
    }

    setUpdatingWaitlistKey(waitlistKey);
    try {
      if (existingEntry) {
        await cancelWaitlist(existingEntry.id);
        alert('줄서기가 취소되었습니다.');
      } else {
        const entry = await joinWaitlist({
          productId,
          productOptionId,
          orderSheetId: activeOrderSheet.id,
          storeId: currentStore.id,
          customerName: buyerProfile.name.trim(),
          customerPhone: buyerProfile.phone.trim(),
          quantity,
        });
        alert(`${entry.remainingQuantity}개 줄서기가 신청되었습니다.`);
        setWaitlistQuantities((current) => ({
          ...current,
          [waitlistKey]: 1,
        }));
      }

      await Promise.all([loadStoreData(), loadMyWaitlists()]);
    } catch (error: any) {
      console.error('Failed to update waitlist', error);
      alert(
        error?.response?.data?.message ||
          '줄서기 처리 중 오류가 발생했습니다.',
      );
      await loadStoreData();
    } finally {
      setUpdatingWaitlistKey(null);
    }
  };

  const calculateCartTotals = (
    sourceCart: Record<string, number>,
    fallbackItems: Record<string, OrderItem> = existingOrderItems,
  ) => {
    return (Object.entries(sourceCart) as [string, number][]).reduce<{
      count: number;
      price: number;
    }>(
      (acc, [id, qty]) => {
        const allOptions = products.flatMap((product) => product.options || []);
        const option = allOptions.find((item) => item.id === id);

        if (option) {
          const product = products.find((item) => item.id === option.productId);
          if (!isSelectableProduct(product)) {
            const fallbackItem = fallbackItems[id];
            if (fallbackItem) {
              acc.count += qty;
              acc.price += fallbackItem.price * qty;
            }
            return acc;
          }
          const price = option.price ?? product?.price ?? 0;
          acc.count += qty;
          acc.price += price * qty;
          return acc;
        }

        const product = products.find((item) => item.id === id);
        if (isSelectableProduct(product)) {
          acc.count += qty;
          acc.price += product.price * qty;
          return acc;
        }

        const fallbackItem = fallbackItems[id];
        if (fallbackItem) {
          acc.count += qty;
          acc.price += fallbackItem.price * qty;
        }

        return acc;
      },
      { count: 0, price: 0 },
    );
  };

  const buildFunnelCartItems = (
    sourceCart: Record<string, number>,
    fallbackItems: Record<string, OrderItem> = existingOrderItems,
  ) =>
    Object.entries(sourceCart)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => {
        const allOptions = products.flatMap((product) => product.options || []);
        const option = allOptions.find((item) => item.id === id);

        if (option) {
          const product = products.find((item) => item.id === option.productId);
          const fallbackItem = fallbackItems[id];
          const productName =
            product?.shortName ||
            product?.name ||
            fallbackItem?.name ||
            option.productId;

          return {
            productId: option.productId,
            productOptionId: option.id,
            productName,
            optionName: option.name,
            quantity: qty,
            price: option.price ?? product?.price ?? fallbackItem?.price ?? 0,
          };
        }

        const product = products.find((item) => item.id === id);
        const fallbackItem = fallbackItems[id];

        return {
          productId: product?.id || fallbackItem?.productId || id,
          productOptionId: fallbackItem?.productOptionId || null,
          productName:
            product?.shortName || product?.name || fallbackItem?.name || id,
          optionName: fallbackItem?.optionName || null,
          quantity: qty,
          price: product?.price ?? fallbackItem?.price ?? 0,
        };
      })
      .slice(0, 30);

  const totals = useMemo(
    () => calculateCartTotals(cart),
    [cart, existingOrderItems, products],
  );

  useEffect(() => {
    if (!currentStore || totals.count === 0) {
      return;
    }

    if (cartUpdateTimerRef.current) {
      window.clearTimeout(cartUpdateTimerRef.current);
    }

    cartUpdateTimerRef.current = window.setTimeout(() => {
      trackFunnelEvent({
        eventType: 'CART_UPDATED',
        cartItemCount: totals.count,
        metadata: {
          skuCount: Object.keys(cart).length,
          totalPrice: totals.price,
          cartItems: buildFunnelCartItems(cart),
        },
      });
    }, 1200);

    return () => {
      if (cartUpdateTimerRef.current) {
        window.clearTimeout(cartUpdateTimerRef.current);
      }
    };
  }, [cart, currentStore, totals.count, totals.price, orderFunnelBasePayload]);

  useEffect(() => {
    if (!isOrderConfirmSheetOpen) {
      return;
    }

    const trimmedName = draftBuyerProfile.name.trim();
    const trimmedPhone = draftBuyerProfile.phone.trim();
    if (!trimmedName || !trimmedPhone) {
      return;
    }

    const eventKey = `${trimmedName}:${trimmedPhone}:${totals.count}`;
    if (lastCustomerInfoEventKeyRef.current === eventKey) {
      return;
    }

    if (customerInfoTimerRef.current) {
      window.clearTimeout(customerInfoTimerRef.current);
    }

    customerInfoTimerRef.current = window.setTimeout(() => {
      lastCustomerInfoEventKeyRef.current = eventKey;
      trackFunnelEvent({
        eventType: 'CUSTOMER_INFO_ENTERED',
        customerName: trimmedName,
        customerPhone: trimmedPhone,
        cartItemCount: totals.count,
      });
    }, 800);

    return () => {
      if (customerInfoTimerRef.current) {
        window.clearTimeout(customerInfoTimerRef.current);
      }
    };
  }, [
    draftBuyerProfile.name,
    draftBuyerProfile.phone,
    isOrderConfirmSheetOpen,
    totals.count,
    orderFunnelBasePayload,
  ]);

  useEffect(() => {
    if (!currentStore) {
      return;
    }

    const handlePageHide = () => {
      sendOrderFunnelBeacon({
        ...orderFunnelBasePayload,
        eventType: 'ORDER_FLOW_EXITED',
        cartItemCount: totals.count,
        customerName:
          draftBuyerProfile.name.trim() || buyerProfile.name.trim() || undefined,
        customerPhone:
          draftBuyerProfile.phone.trim() || buyerProfile.phone.trim() || undefined,
        metadata: {
          hasOpenConfirmSheet: isOrderConfirmSheetOpen,
          skuCount: Object.keys(cart).length,
          cartItems: buildFunnelCartItems(cart),
        },
      });
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [
    buyerProfile.name,
    buyerProfile.phone,
    cart,
    currentStore,
    draftBuyerProfile.name,
    draftBuyerProfile.phone,
    isOrderConfirmSheetOpen,
    orderFunnelBasePayload,
    totals.count,
  ]);

  const mergedCart = useMemo(() => {
    return mergeCartQuantities(existingOrderCart, cart);
  }, [cart, existingOrderCart]);

  const orderDraftCart = cart;

  const orderDraftTotals = useMemo(
    () => calculateCartTotals(orderDraftCart),
    [existingOrderItems, orderDraftCart, products],
  );

  const mergedTotals = useMemo(
    () => calculateCartTotals(mergedCart),
    [existingOrderItems, mergedCart, products],
  );

  const focusOrderConfirmField = (field: 'name' | 'phone') => {
    const targetRef =
      field === 'name' ? orderConfirmNameInputRef : orderConfirmPhoneInputRef;

    window.setTimeout(() => {
      targetRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      targetRef.current?.focus({ preventScroll: true });
    }, 0);
  };

  const buildOrderItemsFromCart = (
    sourceCart: Record<string, number>,
    fallbackItems: Record<string, OrderItem> = existingOrderItems,
  ): OrderItem[] =>
    Object.entries(sourceCart)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => {
        const allOptions = products.flatMap((product) => product.options || []);
        const option = allOptions.find((item) => item.id === id);

        if (option) {
          const product = products.find((item) => item.id === option.productId);
          if (!isSelectableProduct(product)) {
            const fallbackItem = fallbackItems[id];
            if (!fallbackItem) {
              return null;
            }

            return {
              ...fallbackItem,
              quantity: qty,
            };
          }
          return {
            productId: option.productId,
            productOptionId: id,
            name: `${product?.name || ''} (${option.name})`,
            optionName: option.name,
            price: option.price ?? product?.price ?? 0,
            quantity: qty,
            image: product?.image || '',
          };
        }

        const product = products.find((item) => item.id === id);
        if (!isSelectableProduct(product)) {
          const fallbackItem = fallbackItems[id];
          if (!fallbackItem) {
            return null;
          }

          return {
            ...fallbackItem,
            quantity: qty,
          };
        }

        return {
          productId: id,
          name: product.name,
          price: product.price,
          quantity: qty,
          image: product.image || '',
        };
      })
      .filter((item): item is OrderItem => item !== null);

  const buildOrderItems = () => buildOrderItemsFromCart(orderDraftCart);

  const openOrderConfirmSheet = (sourceCart: Record<string, number>) => {
    if (!currentStore) {
      alert('배송지가 올바르지 않거나 설정되지 않았습니다.');
      return;
    }

    if (calculateCartTotals(sourceCart).count === 0) {
      alert('상품을 선택해주세요.');
      return;
    }

    if (isLoadingExistingOrders) {
      alert('기존 주문을 확인 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const nextDraftPickupDate = isPickupTimeCollectionEnabled
      ? selectedPickupDate || pickupDateOptions[0]?.value || ''
      : '';
    const nextDraftPickupHourOptions = isPickupTimeCollectionEnabled
      ? buildPickupHourOptions(
        currentStore.operationStartHour,
        currentStore.operationEndHour,
        nextDraftPickupDate,
        new Date(),
        currentStore.pickupUnmannedStartHour,
        currentStore.pickupUnmannedLabel,
      )
      : [];
    const nextDraftPickupHour = isPickupTimeCollectionEnabled
      ? selectedPickupHour !== '' &&
        nextDraftPickupHourOptions.some(
          (option) => option.value === selectedPickupHour,
        )
        ? selectedPickupHour
        : nextDraftPickupHourOptions[0]?.value ?? ''
      : '';

    setDraftBuyerProfile({
      name: buyerProfile.name,
      phone: buyerProfile.phone,
    });
    setDraftPickupDate(nextDraftPickupDate);
    setDraftPickupHour(nextDraftPickupHour);
    setDraftRequestMessage(isRequestMessageEnabled ? requestMessage : '');
    setOrderSuccessMessage('');
    setOrderSuccessSummary(null);
    setOrderConfirmMode('confirm');
    setIsOrderConfirmSheetOpen(true);
  };

  const closeOrderConfirmSheet = () => {
    if (isSubmittingOrder) {
      return;
    }
    setIsOrderConfirmSheetOpen(false);
    setOrderConfirmMode('confirm');
    setOrderSuccessMessage('');
    setOrderSuccessSummary(null);
  };

  const handleContinueOrdering = () => {
    setIsOrderConfirmSheetOpen(false);
    setOrderConfirmMode('confirm');
    setOrderSuccessMessage('');
    setOrderSuccessSummary(null);
    void loadStoreData();
  };

  const handleViewOrderHistoryAfterSuccess = () => {
    setIsOrderConfirmSheetOpen(false);
    setOrderConfirmMode('confirm');
    setOrderSuccessMessage('');
    setOrderSuccessSummary(null);

    if (isStoreOrder) {
      onViewCart();
      return;
    }

    onViewHistory();
  };

  const handleSubmitOrder = async (
    destination: SubmitDestination,
    overrides: OrderSubmitOverrides = {},
  ) => {
    const submitProfile = overrides.profile ?? draftBuyerProfile;
    const submitPickupDate = overrides.pickupDate ?? draftPickupDate;
    const submitPickupHour = overrides.pickupHour ?? draftPickupHour;
    const submitRequestMessage =
      overrides.requestMessage ?? draftRequestMessage;
    const submitDraftCart = overrides.sourceCart ?? orderDraftCart;
    const submitDraftTotals = calculateCartTotals(submitDraftCart);
    const submitDraftMergedCart = mergeCartQuantities(
      existingOrderCart,
      submitDraftCart,
    );
    const trimmedName = submitProfile.name.trim();
    const trimmedPhone = submitProfile.phone.trim();

    trackFunnelEvent({
      eventType: 'ORDER_SUBMIT_CLICKED',
      customerName: trimmedName || undefined,
      customerPhone: trimmedPhone || undefined,
      cartItemCount: submitDraftTotals.count,
      metadata: {
        destination,
        hasName: Boolean(trimmedName),
        hasPhone: Boolean(trimmedPhone),
        hasStore: Boolean(currentStore),
        cartItems: buildFunnelCartItems(submitDraftMergedCart),
      },
    });

    if (!trimmedName) {
      alert('성함을 입력해주세요.');
      focusOrderConfirmField('name');
      return;
    }

    if (!trimmedPhone) {
      alert('전화번호를 입력해주세요.');
      focusOrderConfirmField('phone');
      return;
    }

    if (!currentStore) {
      alert('배송지가 올바르지 않거나 설정되지 않았습니다.');
      return;
    }

    if (submitDraftTotals.count === 0) {
      alert('상품을 선택해주세요.');
      return;
    }

    let nextPickupLabel = isPickupTimeCollectionEnabled ? savedPickupLabel : null;
    const trimmedRequestMessage = isRequestMessageEnabled
      ? submitRequestMessage.trim()
      : '';

    if (isPickupTimeCollectionEnabled) {
      const resolvedPickupDate = submitPickupDate || pickupDateOptions[0]?.value || '';

      if (pickupDateOptions.length > 0) {
        if (submitPickupHour === '') {
          alert('픽업 시간을 선택해주세요.');
          return;
        }

        const hour = Number(submitPickupHour);
        const localSelection = {
          dateValue: resolvedPickupDate,
          hour,
          label: buildStoredPickupLabel(
            resolvedPickupDate,
            hour,
            getStorePickupHourExtraLabel(currentStore, hour),
          ),
          updatedAt: new Date().toISOString(),
        };

        nextPickupLabel = localSelection.label;
        setDraftPickupDate(resolvedPickupDate);
        setSelectedPickupDate(resolvedPickupDate);
        setSelectedPickupHour(hour);
        setSavedPickupLabel(localSelection.label);

        try {
          const savedSelection = await savePickupSelection(localSelection);
          nextPickupLabel = savedSelection.label;
          setSavedPickupLabel(savedSelection.label);
        } catch (pickupError) {
          console.error(
            'Failed to sync pickup preference during order',
            pickupError,
          );
        }
      }

      if (!nextPickupLabel?.trim()) {
        alert('픽업 일정을 먼저 선택해주세요.');
        return;
      }
    }

    setBuyerProfile((prev) => ({
      ...prev,
      name: trimmedName,
      phone: trimmedPhone,
    }));

    if (isRequestMessageEnabled) {
      saveRequestMessage(trimmedRequestMessage);
      setRequestMessage(trimmedRequestMessage);
    }

    setIsSubmittingOrder(true);
    setSubmitDestination(destination);

    let didSubmitSucceed = false;
    let partialOrderAlertMessage = '';
    let items: OrderItem[] = [];
    let submitMergedCart = submitDraftMergedCart;
    let submitExistingOrderItems = existingOrderItems;
    let submitMergedTotals = calculateCartTotals(submitDraftMergedCart);
    let submitOrderSheetId =
      activeOrderSheet?.id || currentEditableOrderSheetId || undefined;

    const storeRequestText = isStoreOrder
      ? buildStoreOrderRequestText(
        isPickupTimeCollectionEnabled ? nextPickupLabel : null,
        isRequestMessageEnabled ? trimmedRequestMessage : '',
      )
      : '';

    try {
      const latestSnapshot = await resolveEditableOrderSnapshot({
        ...buyerProfile,
        name: trimmedName,
        phone: trimmedPhone,
      });

      submitExistingOrderItems = latestSnapshot.items;
      submitMergedCart = mergeCartQuantities(latestSnapshot.cart, submitDraftCart);
      submitMergedTotals = calculateCartTotals(
        submitMergedCart,
        submitExistingOrderItems,
      );
      submitOrderSheetId =
        activeOrderSheet?.id || latestSnapshot.targetOrderSheetId || undefined;
      items = buildOrderItemsFromCart(submitMergedCart, submitExistingOrderItems);

      setCurrentEditableOrderSheetId(latestSnapshot.targetOrderSheetId);
      setExistingOrderCart(latestSnapshot.cart);
      setExistingOrderItems(latestSnapshot.items);

      if (items.length === 0) {
        alert('주문 가능한 상품이 없어 주문이 접수되지 않았습니다.');
        return;
      }

      trackFunnelEvent({
        eventType: 'ORDER_CREATE_STARTED',
        customerName: trimmedName,
        customerPhone: trimmedPhone,
        cartItemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        metadata: {
          itemSkuCount: items.length,
          totalPrice: submitMergedTotals.price,
          cartItems: buildFunnelCartItems(
            submitMergedCart,
            submitExistingOrderItems,
          ),
        },
      });

      const { order, unavailableItems, resultType } = await createOrder({
        ...(submitOrderSheetId && { orderSheetId: submitOrderSheetId }),
        storeId: currentStore.id,
        storeName: currentStore.name,
        items,
        totalPrice: submitMergedTotals.price,
        customerName: trimmedName,
        customerPhone: trimmedPhone,
        request: storeRequestText || undefined,
        userId: buyerProfile.id,
      });

      if (!order) {
        trackFunnelEvent({
          eventType: 'ORDER_CREATE_FAILED',
          customerName: trimmedName,
          customerPhone: trimmedPhone,
          cartItemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          metadata: {
            reason: 'ALL_ITEMS_UNAVAILABLE',
            unavailableItemCount: unavailableItems.length,
            resultType,
            cartItems: buildFunnelCartItems(
              submitMergedCart,
              submitExistingOrderItems,
            ),
          },
        });
        alert(
          `주문 가능한 상품이 없어 주문이 접수되지 않았습니다.\n\n제외 상품:\n${formatUnavailableItemsMessage(unavailableItems)}`,
        );
        return;
      }

      if (resultType === 'UNCHANGED_WITH_UNAVAILABLE') {
        partialOrderAlertMessage = `재고 부족 또는 주문 불가 상품은 반영되지 않아 기존 주문 상품이 그대로 유지되었습니다.\n\n반영되지 않은 상품:\n${formatUnavailableItemsMessage(unavailableItems)}`;
      } else if (unavailableItems.length > 0) {
        partialOrderAlertMessage = `일부 상품은 품절 또는 주문 불가로 제외되고 나머지만 주문되었습니다.\n\n제외 상품:\n${formatUnavailableItemsMessage(unavailableItems)}`;
      }

      if (trimmedName || trimmedPhone) {
        try {
          await updateProfile({
            name: trimmedName,
            phone: trimmedPhone,
          });
        } catch (profileError) {
          console.error(
            'Failed to update profile during order confirmation',
            profileError,
          );
        }
      }

      setExistingOrderCart(buildCartFromItems(order.items));
      setExistingOrderItems(buildOrderItemMapFromItems(order.items));
      setCurrentEditableOrderSheetId(order.orderSheetId || null);
      if (order.orderSheet) {
        setActiveOrderSheet(order.orderSheet);
      }
      const confirmedItemMap = new Map(
        order.items.map((item) => [getOrderItemCartKey(item), item]),
      );
      const addedItems = buildOrderItemsFromCart(
        submitDraftCart,
        submitExistingOrderItems,
      )
        .map((item) => {
          const key = getOrderItemCartKey(item);
          const confirmedItem = confirmedItemMap.get(key);
          const addedQuantity = Math.max(
            (confirmedItem?.quantity || 0) - (latestSnapshot.cart[key] || 0),
            0,
          );

          if (!confirmedItem || addedQuantity === 0) {
            return null;
          }

          return {
            ...item,
            ...confirmedItem,
            quantity: Math.min(addedQuantity, item.quantity),
          };
        })
        .filter((item): item is OrderItem => item !== null);
      setOrderSuccessSummary({
        items: addedItems,
        totalCount: addedItems.reduce((sum, item) => sum + item.quantity, 0),
        totalPrice: addedItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0,
        ),
      });
      setCart({});
      trackFunnelEvent({
        eventType: 'ORDER_CREATE_SUCCESS',
        orderId: order.id,
        customerName: trimmedName,
        customerPhone: trimmedPhone,
        cartItemCount: order.items.reduce((sum, item) => sum + item.quantity, 0),
        metadata: {
          resultType,
          unavailableItemCount: unavailableItems.length,
          shareToken: shareAttribution.shareToken,
          sharedProductId: shareAttribution.sharedProductId,
          cartItems: order.items.slice(0, 30).map((item) => ({
            productId: item.productId,
            productOptionId: item.productOptionId || null,
            productName: item.name,
            optionName: item.optionName || null,
            quantity: item.quantity,
            price: item.price,
          })),
        },
      });
      resetOrderFunnelSessionId(orderFunnelScope);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      didSubmitSucceed = true;
    } catch (error: any) {
      console.error('Failed to create order', error);
      const errorMessage = error?.response?.data?.message || '';
      trackFunnelEvent({
        eventType: 'ORDER_CREATE_FAILED',
        customerName: trimmedName,
        customerPhone: trimmedPhone,
        cartItemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        metadata: {
          status: error?.response?.status || null,
          message: errorMessage || error?.message || 'Unknown order error',
          cartItems: buildFunnelCartItems(
            submitMergedCart,
            submitExistingOrderItems,
          ),
        },
      });
      if (
        errorMessage.includes('Store is closed') ||
        errorMessage.includes('Ordering is closed') ||
        errorMessage.includes('주문을 마감') ||
        errorMessage.includes('주문 기간이 종료')
      ) {
        alert('현재 주문을 마감한 상태입니다.');
      } else if (errorMessage.includes('have not started yet')) {
        alert(
          '아직 판매가 시작되지 않은 상품이 포함되어 있습니다.\n판매 시작 시간을 확인해주세요.',
        );
      } else if (errorMessage.includes('Stock insufficient')) {
        const productNameMatch = errorMessage.match(
          /Stock insufficient for (.*)\./,
        );
        const productName = productNameMatch ? productNameMatch[1] : '상품';
        alert(`재고가 부족하여 주문이 불가능합니다.\n(${productName})`);
      } else {
        alert('주문 처리 중 오류가 발생했습니다.');
      }
    } finally {
      setIsSubmittingOrder(false);
      setSubmitDestination(null);
    }

    if (!didSubmitSucceed) {
      return;
    }

    setOrderSuccessMessage(partialOrderAlertMessage || '주문이 완료되었습니다.');
    setOrderConfirmMode('success');
    setIsOrderConfirmSheetOpen(true);
  };

  const handleSubmitSelectedCart = async () => {
    if (!currentStore) {
      alert('배송지가 올바르지 않거나 설정되지 않았습니다.');
      return;
    }

    if (totals.count === 0) {
      alert('상품을 선택해주세요.');
      return;
    }

    if (isLoadingExistingOrders) {
      alert('기존 주문을 확인 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    if (!buyerProfile.name.trim() || !buyerProfile.phone.trim()) {
      openOrderConfirmSheet(cart);
      return;
    }

    if (isPickupTimeCollectionEnabled && pickupDateOptions.length > 0) {
      const resolvedPickupDate =
        selectedPickupDate || pickupDateOptions[0]?.value || '';

      if (!resolvedPickupDate || selectedPickupHour === '') {
        openOrderConfirmSheet(cart);
        return;
      }
    }

    await handleSubmitOrder('stay', {
      sourceCart: cart,
      profile: buyerProfile,
      pickupDate: isPickupTimeCollectionEnabled
        ? selectedPickupDate || pickupDateOptions[0]?.value || ''
        : '',
      pickupHour: isPickupTimeCollectionEnabled ? selectedPickupHour : '',
      requestMessage: isRequestMessageEnabled ? requestMessage : '',
    });
  };

  const getOrderableCartKeyForProduct = (product: Product) => {
    if (!isSelectableProduct(product) || product.isClosed) {
      return null;
    }

    const salesStart = product.salesStartDate
      ? new Date(product.salesStartDate)
      : null;
    if (salesStart && salesStart > new Date()) {
      return null;
    }

    const availableOption = product.options?.find((option) => {
      const stock = option.availableStock ?? option.stock ?? 0;
      return stock > 0 && !option.isClosed;
    });

    if (availableOption) {
      return availableOption.id;
    }

    if (product.options?.length) {
      return null;
    }

    const stock = product.availableStock ?? product.totalStock ?? 0;
    return stock > 0 ? product.id : null;
  };

  const handleOrderProductFromDetail = async (product: Product) => {
    const cartKey = getOrderableCartKeyForProduct(product);

    if (!cartKey) {
      alert('현재 주문할 수 없는 상품입니다.');
      return;
    }

    const detailOrderCart = { [cartKey]: 1 };
    setSelectedProductForDetail(null);

    if (!buyerProfile.name.trim() || !buyerProfile.phone.trim()) {
      setCart(detailOrderCart);
      window.setTimeout(() => openOrderConfirmSheet(detailOrderCart), 0);
      return;
    }

    if (isPickupTimeCollectionEnabled && pickupDateOptions.length > 0) {
      const resolvedPickupDate =
        selectedPickupDate || pickupDateOptions[0]?.value || '';

      if (!resolvedPickupDate || selectedPickupHour === '') {
        setCart(detailOrderCart);
        window.setTimeout(() => openOrderConfirmSheet(detailOrderCart), 0);
        return;
      }
    }

    await handleSubmitOrder('stay', {
      sourceCart: detailOrderCart,
      profile: buyerProfile,
      pickupDate: isPickupTimeCollectionEnabled
        ? selectedPickupDate || pickupDateOptions[0]?.value || ''
        : '',
      pickupHour: isPickupTimeCollectionEnabled ? selectedPickupHour : '',
      requestMessage: isRequestMessageEnabled ? requestMessage : '',
    });
  };

  const orderPreviewItems = buildOrderItems();
  const isQuickOrdering = isSubmittingOrder || isLoadingExistingOrders;
  const todayArrivalDateValue = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
  }, []);

  const arrivalDateSourceProducts = useMemo(() => {
    return displayProducts.filter((product) => isSelectableProduct(product));
  }, [displayProducts]);

  const baseFilteredProducts = useMemo(() => {
    return arrivalDateSourceProducts.filter((product) => {
      const matchesTag =
        !activeTag || (product.tags && product.tags.includes(activeTag));
      const matchesSearch =
        !searchQuery ||
        product.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesTag && matchesSearch;
    });
  }, [
    activeTag,
    arrivalDateSourceProducts,
    searchQuery,
  ]);

  const arrivalDateOptions = useMemo(() => {
    if (!isArrivalDateFilterEnabled) {
      return [];
    }

    const sourceProducts = isArrivalDatePriorityStore
      ? arrivalDateSourceProducts
      : baseFilteredProducts;

    const arrivalDateCountMap = new Map<string, number>();

    sourceProducts.forEach((product) => {
      (product.arrivalDateValues || []).forEach((value) => {
        arrivalDateCountMap.set(value, (arrivalDateCountMap.get(value) || 0) + 1);
      });
    });

    return Array.from(arrivalDateCountMap.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([value, count]) => ({
        value,
        label: formatArrivalDateLabel(value),
        count,
      }));
  }, [
    arrivalDateSourceProducts,
    baseFilteredProducts,
    isArrivalDateFilterEnabled,
    isArrivalDatePriorityStore,
  ]);

  const visibleArrivalDateOptions = useMemo(
    () =>
      arrivalDateOptions.filter((option) => option.value >= todayArrivalDateValue),
    [arrivalDateOptions, todayArrivalDateValue],
  );

  useEffect(() => {
    if (!activeArrivalDate) {
      return;
    }

    if (
      !visibleArrivalDateOptions.some(
        (option) => option.value === activeArrivalDate,
      )
    ) {
      setActiveArrivalDate(null);
    }
  }, [activeArrivalDate, visibleArrivalDateOptions]);

  const filteredProducts = useMemo(() => {
    const arrivalDateFilteredProducts = baseFilteredProducts.filter(
      (product) =>
        !activeArrivalDate ||
        (product.arrivalDateValues || []).includes(activeArrivalDate),
    );

    const filtered = arrivalDateFilteredProducts;

    const now = new Date();
    const getIsAvailable = (product: Product) => {
      const salesStart = product.salesStartDate
        ? new Date(product.salesStartDate)
        : null;
      const isNotStarted = salesStart && salesStart > now;
      if (product.isClosed || isNotStarted) {
        return false;
      }

      const hasOptions = product.options && product.options.length > 0;
      if (hasOptions) {
        return product.options!.some((option) => {
          const stock = option.availableStock ?? option.stock ?? 0;
          return stock > 0 && !option.isClosed;
        });
      }

      const stock = product.availableStock ?? product.totalStock ?? 0;
      return stock > 0;
    };

    const getTimestamp = (value?: string) => {
      if (!value) {
        return 0;
      }

      const timestamp = new Date(value).getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    };

    const getBumpedDate = (product: Product) => {
      return getTimestamp(product.createdAt);
    };

    const getLatestUpdatedDate = (product: Product) => {
      return getTimestamp(product.updatedAt);
    };

    const getEarliestArrivalDate = (product: Product) => {
      const sortedArrivalDates = sortArrivalDateValues(product.arrivalDateValues);
      return sortedArrivalDates[0] || '9999-12-31';
    };

    return [...filtered].sort((a, b) => {
      const aAvailable = getIsAvailable(a);
      const bAvailable = getIsAvailable(b);
      if (aAvailable !== bAvailable) {
        return aAvailable ? -1 : 1;
      }

      const bumpedDateCompare = getBumpedDate(b) - getBumpedDate(a);
      if (bumpedDateCompare !== 0) {
        return bumpedDateCompare;
      }

      const latestUpdatedDateCompare =
        getLatestUpdatedDate(b) - getLatestUpdatedDate(a);
      if (latestUpdatedDateCompare !== 0) {
        return latestUpdatedDateCompare;
      }

      const arrivalDateCompare = getEarliestArrivalDate(a).localeCompare(
        getEarliestArrivalDate(b),
      );
      if (arrivalDateCompare !== 0) {
        return arrivalDateCompare;
      }

      return a.name.localeCompare(b.name, 'ko-KR');
    });
  }, [
    activeArrivalDate,
    baseFilteredProducts,
  ]);

  const nextDeliveryDate = useMemo(() => {
    if (!currentStore || currentStore.orderType === 'STORE') return null;

    const now = new Date();
    const cutoffDays = currentStore.orderCutoffDays || 0;
    const deliveryDay =
      currentStore.defaultDeliveryDay ?? currentStore.deliveryDayOfWeek;
    if (deliveryDay === undefined) return null;

    const delivery = new Date();
    delivery.setHours(0, 0, 0, 0);

    const daysToDelivery = (deliveryDay - delivery.getDay() + 7) % 7;
    delivery.setDate(delivery.getDate() + daysToDelivery);

    const cutoff = new Date(delivery);
    cutoff.setDate(cutoff.getDate() - cutoffDays);
    cutoff.setHours(0, 0, 0, 0);

    if (now >= cutoff) {
      delivery.setDate(delivery.getDate() + 7);
    }

    return delivery;
  }, [currentStore]);

  const activeArrivalDateLabel = activeArrivalDate
    ? formatArrivalDateLabel(activeArrivalDate)
    : null;

  if (isLoadingStore) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-emerald-100 border-t-emerald-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (storeError === 'STORE_NOT_FOUND') {
    return <NotFoundPage />;
  }

  if (storeError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-10 text-center">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center text-red-500 mb-6">
          <Info size={40} />
        </div>
        <h3 className="text-xl font-black text-gray-900 mb-2">
          데이터를 불러오지 못했습니다
        </h3>
        <p className="text-gray-500 font-bold leading-relaxed mb-8">
          잠시 후 다시 시도해주세요.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-8 h-14 bg-gray-900 text-white font-bold rounded-2xl"
        >
          새로고침
        </button>
      </div>
    );
  }

  const handleShareStore = async () => {
    const identifier = currentStore?.slug?.trim() || resolvedStoreId;
    // 공유자 → 유입/주문 어트리뷰션용 불투명 토큰 (userId는 URL에 노출하지 않음)
    const shareToken = createShareToken();
    const storeShareUrl = new URL(
      `/store/${encodeURIComponent(identifier)}`,
      window.location.origin,
    );
    storeShareUrl.searchParams.set(SHARE_TOKEN_PARAM, shareToken);
    const storeLink = storeShareUrl.toString();
    const shareTitle = currentStore?.name || '공동구매';
    const shareDescription =
      currentStore?.owner?.shareDescription?.trim() ||
      currentStore?.description?.trim() ||
      `${shareTitle} 공동구매에 참여해보세요.`;
    trackFunnelEvent({
      eventType: 'PRODUCT_SHARE_CREATED',
      metadata: {
        shareType: 'store',
        source: 'kakao',
        campaign: 'store-share',
        shareToken,
        url: storeLink,
      },
    });

    const didOpenKakaoShare = shareKakao({
      title: shareTitle,
      description: shareDescription,
      url: storeLink,
      campaign: 'store-share',
      variant: 'text',
    });

    if (didOpenKakaoShare) {
      return;
    }

    try {
      await navigator.clipboard.writeText(storeLink);
      alert('카카오 공유를 사용할 수 없어 매장 링크를 복사했습니다.');
    } catch (error) {
      console.error('Failed to copy store share link', error);
      alert('카카오 공유를 사용할 수 없습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col max-w-lg mx-auto bg-white shadow-xl relative pb-20">
      <Header
        storeName={currentStore?.name || '배송지 선택 필요'}
        onShare={handleShareStore}
        onStoreInfoClick={() => setIsStoreInfoOpen(true)}
        onMyInfoClick={() => setIsMyInfoOpen(true)}
      />

      <main className="flex-1">
        <div className="py-[7px] px-6 border-b bg-gray-50/50">
          {isStoreOrder ? (
            <div className="bg-white border border-gray-100 rounded-[20px] p-4 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 shrink-0">
                <Clock size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 text-[13px] font-black text-gray-900 leading-tight break-words">
                    [{currentStore?.name || '미지정'}] {activeOrderSheet?.label || '매장 주문서'}
                  </div>
                  {hasOrderSheetSummaryDetails && (
                    <button
                      type="button"
                      onClick={() =>
                        setIsOrderSheetSummaryExpanded((prev) => !prev)
                      }
                      className="shrink-0 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-black text-gray-500 transition-colors hover:bg-gray-100"
                    >
                      {isOrderSheetSummaryExpanded ? '접기' : '더보기'}
                      {isOrderSheetSummaryExpanded ? (
                        <ChevronUp size={13} />
                      ) : (
                        <ChevronDown size={13} />
                      )}
                    </button>
                  )}
                </div>
                {isOrderSheetSummaryExpanded && (
                  <div className="mt-1.5 flex flex-col gap-0.5">
                    {orderSheetSummaryLines.map((line) => (
                      <div
                        key={line.label}
                        className="text-[11px] font-bold text-gray-400"
                      >
                        {line.label}:{' '}
                        <span className={line.valueClassName}>{line.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : nextDeliveryDate ? (
            <div className="bg-white border border-gray-100 rounded-[20px] p-4 shadow-sm flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 shrink-0">
                <Clock size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 text-[13px] font-black text-gray-900 leading-tight break-words">
                    [{currentStore?.name || '미지정'}]{' '}
                    {activeOrderSheet?.label ||
                      nextDeliveryDate.toLocaleDateString('ko-KR', {
                        month: 'long',
                        day: 'numeric',
                        weekday: 'short',
                      }) + ' 배송 주문서'}
                  </div>
                  {hasOrderSheetSummaryDetails && (
                    <button
                      type="button"
                      onClick={() =>
                        setIsOrderSheetSummaryExpanded((prev) => !prev)
                      }
                      className="shrink-0 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-black text-gray-500 transition-colors hover:bg-gray-100"
                    >
                      {isOrderSheetSummaryExpanded ? '접기' : '더보기'}
                      {isOrderSheetSummaryExpanded ? (
                        <ChevronUp size={13} />
                      ) : (
                        <ChevronDown size={13} />
                      )}
                    </button>
                  )}
                </div>
                {isOrderSheetSummaryExpanded && (
                  <div className="mt-1.5 flex flex-col gap-0.5">
                    {orderSheetSummaryLines.map((line) => (
                      <div
                        key={line.label}
                        className="text-[11px] font-bold text-gray-400"
                      >
                        {line.label}:{' '}
                        <span className={line.valueClassName}>{line.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <div className="sticky top-[64px] bg-white/95 backdrop-blur-md z-[39] border-b divide-y divide-gray-50">
          <div className="px-6 py-3 flex items-center justify-between gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="상품 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={(e) => {
                  setTimeout(() => {
                    const inputElement = e.target as HTMLInputElement;
                    const y =
                      inputElement.getBoundingClientRect().top +
                      window.scrollY -
                      80;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                  }, 300);
                }}
                className="w-full h-10 pl-10 pr-4 bg-gray-100/80 border-transparent rounded-xl text-[14px] focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </div>
            </div>
          </div>
          {isArrivalDateFilterEnabled && visibleArrivalDateOptions.length > 0 && (
            <div className="border-t border-gray-100 bg-gradient-to-b from-slate-50 to-white px-4 py-3">
              <div className="flex overflow-x-auto no-scrollbar gap-3">
                <div className="min-w-[74px] shrink-0">
                  <button
                    onClick={() => setActiveArrivalDate(null)}
                    className={`w-full rounded-[28px] border px-3 py-2 text-left transition-all ${!activeArrivalDate
                      ? 'border-blue-500 bg-blue-600 text-white shadow-lg shadow-blue-100'
                      : 'border-gray-200 bg-white text-gray-900 shadow-sm'
                      }`}
                  >
                    <div className="text-[15px] text-center font-black leading-tight whitespace-nowrap">
                      전체
                    </div>
                  </button>
                </div>
                {visibleArrivalDateOptions.map((option) => {
                  const isActive = activeArrivalDate === option.value;

                  return (
                    <div
                      key={option.value}
                      className="min-w-[115px] shrink-0"
                    >
                      <button
                        onClick={() => setActiveArrivalDate(option.value)}
                        className={`w-full rounded-[28px] border px-3 py-2 text-left transition-all ${isActive
                          ? 'border-blue-500 bg-blue-600 text-white shadow-lg shadow-blue-100'
                          : 'border-gray-200 bg-white text-gray-900 shadow-sm'
                          }`}
                      >
                        <div className="text-[15px] text-center font-black leading-tight whitespace-nowrap">
                          {option.label}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {availableTags.length > 0 && (
            <div className="flex overflow-x-auto no-scrollbar px-6 py-2.5 gap-x-2 bg-gray-50/30">
              <button
                onClick={() => setActiveTag(null)}
                className={`px-3.5 h-7 rounded-lg text-[11px] font-bold whitespace-nowrap border transition-all ${!activeTag
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-400 border-gray-200'
                  }`}
              >
                #전체
              </button>
              {availableTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => setActiveTag(activeTag === tag.name ? null : tag.name)}
                  className={`px-3.5 h-7 rounded-lg text-[11px] font-bold whitespace-nowrap border transition-all ${activeTag === tag.name
                    ? 'bg-emerald-500 text-white border-emerald-500'
                    : 'bg-white text-gray-400 border-gray-200'
                    }`}
                >
                  #{tag.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 bg-gray-50 flex items-center justify-between border-b border-gray-100">
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px] font-bold text-gray-600">
              총 <span className="text-emerald-500 font-black">{filteredProducts.length}</span>
              개의 상품
            </span>
            {activeArrivalDateLabel && (
              <span className="text-[11px] font-black text-sky-600">
                현재 입고일: {activeArrivalDateLabel}
              </span>
            )}
          </div>
          <span className="text-[12px] font-black text-gray-900">
            선택 {totals.count}개
          </span>
        </div>

        <div className="flex flex-col">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              cart={cart}
              onSetQuantity={handleSetQuantity}
              onViewDetail={setSelectedProductForDetail}
              showArrivalDateBadges={isArrivalDateFilterEnabled}
              orderedQuantities={existingOrderCart}
              isOrdering={isQuickOrdering}
              waitlistsByKey={waitlistsByKey}
              waitlistQuantities={waitlistQuantities}
              updatingWaitlistKey={updatingWaitlistKey}
              onSetWaitlistQuantity={(productId, productOptionId, quantity) => {
                const key = productOptionId || productId;
                setWaitlistQuantities((current) => ({
                  ...current,
                  [key]: quantity,
                }));
              }}
              onToggleWaitlist={handleToggleWaitlist}
            />
          ))}

          {filteredProducts.length === 0 && (
            <div className="py-20 px-10 text-center">
              <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-600 mx-auto mb-6">
                <Package size={40} />
              </div>
              {products.length > 0 &&
                (searchQuery || activeTag || activeArrivalDate) ? (
                <>
                  <h3 className="text-xl font-black text-gray-900 mb-2">
                    조건에 맞는 상품이 없습니다
                  </h3>
                  <p className="text-gray-500 font-bold leading-relaxed">
                    선택한 검색어 또는 필터에 맞는 상품이 없습니다.
                    <br />
                    다른 조건으로 다시 확인해보세요.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-black text-gray-900 mb-2">
                    현재는 준비 중입니다
                  </h3>
                  <p className="text-gray-500 font-bold leading-relaxed">
                    이번 주 주문이 종료되었습니다.
                    <br />
                    다음 주 공동구매를 기대해주세요!
                  </p>
                </>
              )}
            </div>
          )}
        </div>

      </main>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-lg z-[1001] bg-white border-t p-4 flex space-x-3 shadow-lg">
        <button
          onClick={isStoreOrder ? onViewCart : onViewHistory}
          className="px-6 h-14 rounded-2xl border border-gray-200 text-gray-600 font-bold active:bg-gray-50 transition-colors"
        >
          주문내역
        </button>
        <button
          onClick={handleSubmitSelectedCart}
          disabled={isSubmittingOrder || isLoadingExistingOrders || totals.count === 0}
          className={`flex-1 h-14 rounded-2xl px-4 text-lg font-black transition-all active:scale-[0.98] ${
            isSubmittingOrder || isLoadingExistingOrders || totals.count === 0
              ? 'bg-gray-100 text-gray-400'
              : 'bg-emerald-500 text-white shadow-lg shadow-emerald-100'
          }`}
        >
          {isSubmittingOrder
            ? '주문 처리 중...'
            : isLoadingExistingOrders
              ? '확인 중...'
              : `${totals.count}개 주문하기`}
        </button>
      </div>

      {isOrderConfirmSheetOpen && (
        <div className="fixed inset-0 z-[1100] flex items-end justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={closeOrderConfirmSheet}
          />
          <div className="relative w-full max-w-lg max-h-[92dvh] overflow-y-auto rounded-t-[32px] bg-white shadow-2xl animate-in slide-in-from-bottom duration-300 custom-scrollbar">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white/95 px-6 py-4 backdrop-blur-md">
              <div>
                <h2 className="text-lg font-black text-gray-900">
                  {orderConfirmMode === 'success' ? '주문 완료' : '주문 정보 확인'}
                </h2>
                <p className="text-[12px] font-bold text-gray-400">
                  {orderConfirmMode === 'success'
                    ? '계속 주문하거나 주문내역을 확인할 수 있습니다.'
                    : '주문 전에 고객 정보와 요청사항을 확인해주세요.'}
                </p>
              </div>
              <button
                onClick={closeOrderConfirmSheet}
                disabled={isSubmittingOrder}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-500 disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>

            {orderConfirmMode === 'success' ? (
              <div className="mx-6 mt-5 space-y-4">
                <div className="rounded-[24px] border border-emerald-100 bg-emerald-50 p-5">
                  <p className="mt-2 whitespace-pre-line text-[13px] font-bold leading-relaxed text-gray-600">
                    {orderSuccessMessage}
                  </p>
                </div>

                {orderSuccessSummary && orderSuccessSummary.items.length > 0 && (
                  <div className="rounded-[24px] border border-gray-200 bg-white p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-black text-gray-900">
                          추가된 주문
                        </h3>
                        <p className="text-[11px] font-bold text-gray-400">
                          방금 주문내역에 더해진 상품입니다.
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-[11px] font-black text-emerald-600">
                          총 {orderSuccessSummary.totalCount}개
                        </div>
                        <div className="text-sm font-black text-gray-900">
                          {orderSuccessSummary.totalPrice.toLocaleString()}원
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {orderSuccessSummary.items.slice(0, 4).map((item) => {
                        const optionLabel = getOrderPreviewOptionLabel(item, products);
                        const displayName = getOrderPreviewName(item, optionLabel);
                        const imageUrl = sanitizeImageUrl(item.image);

                        return (
                          <div
                            key={`${item.productId}-${item.productOptionId || 'base'}`}
                            className="flex items-center gap-3 rounded-2xl bg-gray-50 px-3 py-3"
                          >
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={displayName}
                                className="h-12 w-12 rounded-2xl object-cover"
                              />
                            ) : (
                              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                                <Package size={18} />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[13px] font-black text-gray-900">
                                {displayName}
                              </div>
                              {optionLabel && (
                                <div className="mt-0.5 truncate text-[10px] font-black text-emerald-600">
                                  옵션: {optionLabel}
                                </div>
                              )}
                              <div className="mt-0.5 text-[11px] font-bold text-gray-400">
                                {item.price.toLocaleString()}원 x {item.quantity}개
                              </div>
                            </div>
                            <div className="shrink-0 text-right text-[13px] font-black text-gray-900">
                              {(item.price * item.quantity).toLocaleString()}원
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {orderSuccessSummary.items.length > 4 && (
                      <div className="mt-3 rounded-2xl bg-gray-50 px-3 py-2 text-center text-[11px] font-black text-gray-500">
                        외 {orderSuccessSummary.items.length - 4}개 상품이 더 추가되었습니다
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
            <>
            <div className="mx-6 mt-5 rounded-[24px] border border-gray-200 bg-gray-50/80 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-black text-gray-900">선택 상품</h3>
                  <p className="text-[11px] font-bold text-gray-400">
                    이번에 바로 주문할 상품입니다.
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-[11px] font-black text-emerald-600">
                    총 {orderDraftTotals.count}개
                  </div>
                  <div className="text-sm font-black text-gray-900">
                    {orderDraftTotals.price.toLocaleString()}원
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {orderPreviewItems.map((item) => (
                  (() => {
                    const optionLabel = getOrderPreviewOptionLabel(item, products);
                    const displayName = getOrderPreviewName(item, optionLabel);
                    const imageUrl = sanitizeImageUrl(item.image);

                    return (
                      <div
                        key={`${item.productId}-${item.productOptionId || 'base'}`}
                        className="flex items-center gap-3 rounded-2xl border border-white bg-white px-3 py-3"
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={displayName}
                            className="h-14 w-14 rounded-2xl object-cover"
                          />
                        ) : (
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                            <Package size={20} />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-black text-gray-900">
                            {displayName}
                          </div>
                          {optionLabel && (
                            <div className="mt-1 truncate text-[10px] font-black text-emerald-600">
                              옵션: {optionLabel}
                            </div>
                          )}
                          <div className="mt-1 text-[11px] font-bold text-gray-400">
                            {item.price.toLocaleString()}원 x {item.quantity}개
                          </div>
                        </div>
                        <div className="shrink-0 text-right text-[13px] font-black text-gray-900">
                          {(item.price * item.quantity).toLocaleString()}원
                        </div>
                      </div>
                    );
                  })()
                ))}
              </div>
            </div>

            <OrderForm
              totalCount={orderDraftTotals.count}
              totalPrice={orderDraftTotals.price}
              profile={draftBuyerProfile}
              nameInputRef={orderConfirmNameInputRef}
              phoneInputRef={orderConfirmPhoneInputRef}
              onProfileChange={(updates) =>
                setDraftBuyerProfile((prev) => ({ ...prev, ...updates }))
              }
              isStoreOrder={isStoreOrder}
              showPickupSection={isPickupTimeCollectionEnabled}
              showRequestMessageSection={isRequestMessageEnabled}
              pickupLabel={orderConfirmPickupLabel}
              pickupPolicyText={
                currentStore ? formatStoreOperationSummary(currentStore) : undefined
              }
              pickupDateOptions={pickupDateOptions}
              pickupHourOptions={orderConfirmPickupHourOptions}
              selectedPickupDate={draftPickupDate}
              selectedPickupHour={draftPickupHour}
              showPickupSaveButton={false}
              onPickupDateChange={setDraftPickupDate}
              onPickupHourChange={setDraftPickupHour}
              requestValue={draftRequestMessage}
              onRequestChange={setDraftRequestMessage}
              showConsent={false}
            />
            </>
            )}

            <div className="sticky bottom-0 border-t bg-white/95 px-6 pb-[calc(env(safe-area-inset-bottom)+24px)] pt-4 backdrop-blur-md">
              {orderConfirmMode === 'success' ? (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleContinueOrdering}
                    className="flex h-14 items-center justify-center rounded-2xl border border-gray-200 bg-white text-sm font-black text-gray-700 transition-all active:scale-[0.98]"
                  >
                    계속 주문하기
                  </button>
                  <button
                    onClick={handleViewOrderHistoryAfterSuccess}
                    className="flex h-14 items-center justify-center rounded-2xl bg-emerald-500 text-sm font-black text-white shadow-lg shadow-emerald-100 transition-all active:scale-[0.98]"
                  >
                    주문내역 확인
                  </button>
                </div>
              ) : (
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={() => handleSubmitOrder('stay')}
                  disabled={isSubmittingOrder}
                  className={`flex h-14 items-center justify-center rounded-2xl border text-sm font-black transition-all active:scale-[0.98] ${isSubmittingOrder
                    ? 'border-gray-200 bg-gray-100 text-gray-400'
                    : 'border-gray-200 bg-white text-gray-700'
                    }`}
                >
                  {isSubmittingOrder && submitDestination === 'stay'
                    ? '주문 처리 중...'
                    : '주문 확정하기'}
                </button>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ProductDetailModal
        product={selectedProductForDetail}
        storeId={resolvedStoreId}
        storeName={currentStore?.name}
        orderSheetId={activeOrderSheet?.id}
        isOpen={!!selectedProductForDetail}
        onClose={() => setSelectedProductForDetail(null)}
        onOrderNow={handleOrderProductFromDetail}
        buyerProfile={buyerProfile}
        waitlistEntry={
          selectedProductForDetail
            ? waitlistsByKey[selectedProductForDetail.id]
            : undefined
        }
        isWaitlistUpdating={
          Boolean(selectedProductForDetail) &&
          updatingWaitlistKey === selectedProductForDetail?.id
        }
        waitlistQuantity={
          selectedProductForDetail
            ? waitlistQuantities[selectedProductForDetail.id] || 1
            : 1
        }
        onSetWaitlistQuantity={(quantity) => {
          if (!selectedProductForDetail) return;
          setWaitlistQuantities((current) => ({
            ...current,
            [selectedProductForDetail.id]: quantity,
          }));
        }}
        onToggleWaitlist={(productId, quantity) =>
          void handleToggleWaitlist(productId, undefined, quantity)
        }
        onShareCreated={(product, url, shareToken) => {
          trackFunnelEvent({
            eventType: 'PRODUCT_SHARE_CREATED',
            metadata: {
              shareType: 'product',
              productId: product.id,
              sharedProductId: product.id,
              productName: product.name,
              source: 'kakao',
              campaign: 'product-share',
              shareToken,
              url,
            },
          });
        }}
      />
      <StoreInfoModal
        isOpen={isStoreInfoOpen}
        onClose={() => setIsStoreInfoOpen(false)}
        store={currentStore}
      />
      <MyInfoModal
        isOpen={isMyInfoOpen}
        onClose={() => setIsMyInfoOpen(false)}
        profile={buyerProfile}
        onProfileUpdate={(updated) =>
          setBuyerProfile((prev) => ({ ...prev, ...updated }))
        }
      />
      <Footer />
    </div>
  );
};

export default OrderPage;
